import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { toastSyncSuccess } from 'src/utils/syncToast'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'src/contexts/auth'
import { CreateAnimalForm } from 'src/@types/validations/Animal'
import { useUpdateDiagnosisWithBirth } from 'src/hooks/api/pregnancy-diagnosis'
import { IAnimal } from 'src/dto/animal.dto'
import { SaveDiagnosisWithBirthParams } from 'src/services/api/pregnancy-diagnosis.service'
import { AnimalController } from 'src/controllers/animal.controller'
import { PregnancyDiagnosisController } from 'src/controllers/pregnancy-diagnosis.controller'
import { BirthInfo } from '../../components/DiagnosisModal'
import ROUTES from 'src/constants/routes'
import { calculateGestationTime } from '../../utils/functions'
import { PregnancyDiagnosisFormParams } from '..'
import { useQueryClient } from '@tanstack/react-query'

export const usePregnancyDiagnosisForm = () => {
  const { userFarm: farmId } = useAuth()
  const navigate = useNavigate()
  const [selectedAnimal, setSelectedAnimal] = useState<IAnimal | null>(null)
  const [savedBirths, setSavedBirths] = useState<CreateAnimalForm[]>([])
  const [isDiagnosisModalOpen, setIsDiagnosisModalOpen] = useState(false)
  const [useDateInterval, setUseDateInterval] = useState(false)
  const [isLoadingAnimal, setIsLoadingAnimal] = useState(false)
  const [showDiagnosisWarningModal, setShowDiagnosisWarningModal] = useState(false)

  const { mutateAsync: saveDiagnosisComplete } = useUpdateDiagnosisWithBirth()

  const queryClient = useQueryClient()

  const {
    watch,
    setValue,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: {}
  } = useFormContext<PregnancyDiagnosisFormParams>()

  const handleSelectAnimal = async (animalId: string) => {
    if (!animalId) {
      setSelectedAnimal(null)
      return
    }

    setIsLoadingAnimal(true)
    try {
      const animalData = await queryClient.fetchQuery({
        queryKey: ['getAnimalById', farmId, animalId],
        queryFn: () => new AnimalController().getAnimalById(farmId!, animalId)
      })

      if (animalData) {
        const diagnosisList = await queryClient.fetchQuery({
          queryKey: ['getPregnancyDiagnosisListByAnimalId', farmId, animalId],
          queryFn: () => new PregnancyDiagnosisController().getPregnancyDiagnosisListByAnimalId(farmId!, animalId)
        })

        const pendingDiagnosis = diagnosisList.find((diagnosis) => diagnosis.status !== 'finalizado')

        if (pendingDiagnosis) {
          const url = ROUTES.REPRODUCTION.toPregnancyListDetails(
            pendingDiagnosis.idFazenda,
            pendingDiagnosis.idAnimal,
            pendingDiagnosis.idDiagGest
          )

          navigate(url)
          return
        }

        setShowDiagnosisWarningModal(true)
        setSelectedAnimal(animalData)
        setValue('idAnimal', animalId)
        clearErrors('idAnimal')
      }
    } catch (error) {
      toast.error('Erro ao buscar informações do animal')
      console.error(error)
    } finally {
      setIsLoadingAnimal(false)
    }
  }

  const handleConfirmWarningDiagnosisModal = () => {
    setShowDiagnosisWarningModal(false)
  }

  const handleCancelWarningDiagnosisModal = () => {
    setShowDiagnosisWarningModal(false)
    reset()
    setSelectedAnimal(null)
  }

  const handleToggleDateInterval = () => {
    const nextUseDateInterval = !useDateInterval
    setUseDateInterval(nextUseDateInterval)

    if (nextUseDateInterval) {
      // Quando ativar intervalo, definir data início como ontem e data fim como hoje
      const beginDate = new Date()
      beginDate.setDate(beginDate.getDate() - 1)
      const endDate = new Date()

      setValue('dataInicial', beginDate.toISOString())
      setValue('dataFim', endDate.toISOString())
    } else {
      // Quando desativar, voltar para data de hoje
      setValue('dataCobertura', new Date().toISOString())
    }
  }

  const handleOpenDiagnosisModal = () => {
    const animalId = watch('idAnimal')
    const tipoReproducao = watch('tipoReproducao')
    const touro = watch('touro')

    if (!animalId) {
      setError('idAnimal', { message: 'Vaca é obrigatório' })
      return
    }

    if (!tipoReproducao) {
      setError('tipoReproducao', { message: 'Selecione o tipo de reprodução' })
      return
    }

    if (!touro) {
      setError('touro', { message: 'Touro é obrigatório' })
      return
    }

    setIsDiagnosisModalOpen(true)
  }

  const handleSaveBirths = (births: CreateAnimalForm[], birthInfo: BirthInfo) => {
    setSavedBirths(births)
    setValue('dataParto', birthInfo.dataParto)
    setValue('tipoParto', birthInfo.tipoParto)
    setIsDiagnosisModalOpen(false)
  }

  const handleCloseDiagnosisModal = () => {
    setIsDiagnosisModalOpen(false)
  }

  const handleRemoveBirth = (index: number) => {
    setSavedBirths((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSaveAll = handleSubmit(async (diagnosis) => {
    const diagnostico = watch('diagnostico')

    // Se for prenha e tiver crias, exigir data de diagnóstico e data de parto
    if (diagnostico === 'prenha' && savedBirths.length > 0) {
      if (!diagnosis.dataDiagnostico) {
        setError('dataDiagnostico', {
          message: 'Data do diagnóstico é obrigatória para diagnóstico prenha'
        })
        return
      }
      if (!diagnosis.dataParto) {
        toast.error('Data do parto é obrigatória quando há crias cadastradas')
        return
      }
    }

    // Se tiver diagnóstico (prenha/vazia), exigir data do diagnóstico
    if (diagnostico && !diagnosis.dataDiagnostico) {
      setError('dataDiagnostico', { message: 'Data do diagnóstico é obrigatória' })
      return
    }

    try {
      // Calcular data de reprodução (média quando for intervalo)
      let dataReproducao: Date
      if (useDateInterval) {
        const dataInicio = new Date(diagnosis.dataInicial || '')
        const dataFim = new Date(diagnosis.dataFim || '')
        const beginTime = dataInicio.getTime()
        const endTime = dataFim.getTime()
        const averageTime = beginTime + (endTime - beginTime) / 2
        dataReproducao = new Date(averageTime)
      } else {
        dataReproducao = new Date(diagnosis.dataCobertura || '')
      }

      // Calcular tempo de gestação se tiver data do parto
      let tempoGestacao: number | null = null
      if (diagnosis.dataParto && diagnostico === 'prenha') {
        tempoGestacao = calculateGestationTime(diagnosis.dataParto || undefined, dataReproducao.toISOString())
      }

      // Preparar payload do diagnóstico
      const diagnosisPayload: SaveDiagnosisWithBirthParams = {
        idFazenda: farmId!,
        idAnimal: diagnosis.idAnimal,
        touro: diagnosis.touro,
        tipoReproducao: diagnosis.tipoReproducao,
        dataInicial: dataReproducao.toISOString(),
        dataCobertura: dataReproducao.toISOString(),
        dataDiagnostico: diagnostico ? diagnosis.dataDiagnostico : null,
        diagnostico: diagnostico || null,
        dataParto: diagnostico === 'prenha' && savedBirths.length > 0 ? diagnosis.dataParto : undefined,
        tipoParto: diagnostico === 'prenha' && savedBirths.length > 0 ? diagnosis.tipoParto || 'normal' : undefined,
        tempoGestacao: tempoGestacao,
        nascimento: diagnostico === 'prenha' ? savedBirths : []
      }

      await saveDiagnosisComplete(diagnosisPayload)
      toastSyncSuccess('Salvo com sucesso!')

      // Limpar formulário
      reset()
      setSavedBirths([])
      setSelectedAnimal(null)
      setUseDateInterval(false)
    } catch (error) {
      console.error('Erro ao salvar diagnóstico:', error)
      toast.error('Erro ao salvar diagnóstico')
    }
  })

  return {
    // State
    selectedAnimal,
    savedBirths,
    setSavedBirths,
    isDiagnosisModalOpen,
    useDateInterval,
    isLoadingAnimal,
    showDiagnosisWarningModal,

    // Actions
    handleSelectAnimal,
    handleToggleDateInterval,
    handleOpenDiagnosisModal,
    handleSaveBirths,
    handleCloseDiagnosisModal,
    handleSaveAll,
    handleRemoveBirth,
    setShowDiagnosisWarningModal,
    handleConfirmWarningDiagnosisModal,
    handleCancelWarningDiagnosisModal
  }
}
